/**
 * InsightQuote Component - Highlighted Quote Display
 * @module features/results/components/InsightQuote
 *
 * Displays a citation in a highlighted card format
 * with decorative quote marks and italic styling.
 */

import { cn } from '@/lib/utils';
import { Quote } from 'lucide-react';

export interface InsightQuoteProps {
  /** The quote text to display */
  text: string;
}

/**
 * InsightQuote displays a citation in card format
 *
 * Returns null if text is empty or whitespace only.
 *
 * @example
 * ```tsx
 * <InsightQuote text="Um padrão comum em diagnósticos..." />
 * ```
 */
export function InsightQuote({ text }: InsightQuoteProps) {
  // Don't render if text is empty or only whitespace
  if (!text || !text.trim()) {
    return null;
  }

  return (
    <div
      data-testid="insight-quote-card"
      className={cn('rounded-lg border border-muted bg-muted/30', 'p-4 md:p-6', 'relative')}
    >
      <Quote
        data-testid="quote-decoration"
        className="absolute top-3 left-3 h-6 w-6 text-muted-foreground/40"
        aria-hidden="true"
      />
      <blockquote className="pl-8">
        <p className="italic text-muted-foreground">{text}</p>
      </blockquote>
    </div>
  );
}

export default InsightQuote;
